/**
 * Build a React `key` that changes whenever a NativeWind-styled element's
 * class set changes — forcing a fresh mount instead of an in-place restyle.
 *
 * ## Why this exists
 *
 * On native, swapping a RUNTIME-PROCESSED NativeWind class on a live element
 * (`shadow-*`, `opacity-*`, `bg-x/70` and friends, `rotate-*`) can throw
 * react-navigation's **"Couldn't find a navigation context"** as a bogus
 * Render Error — nothing in the tree calls `useNavigation`; the interop layer
 * simply trips over the restyle mid-commit. It is an open upstream bug
 * (nativewind#1536 / #1557 / #1711 / #1712, react-native-css#264 / #269),
 * unfixed as of nativewind 4.2.6 / react-native-css-interop 0.2.6, so
 * upgrading does NOT make it go away. It bites hardest when the swap lands
 * during another commit (a list relayout, a modal teardown).
 *
 * Two sanctioned fixes:
 *   1. Keep the className CONSTANT and toggle a plain inline style instead —
 *      best when the delta is one or two properties (`SectionHeader`'s
 *      chevron rotation).
 *   2. Keep the className conditional but make the flip a REMOUNT with this
 *      helper — best when the delta is a whole variant/state (a Button's
 *      pressed chip, a menu row's selected wash), where hand-porting every
 *      class to inline styles would fork the design system.
 *
 * Prefer (1) for a single property; reach for (2) when a component's own
 * variant prop drives the change (CVA output can't be toggled inline).
 *
 * ## Cost
 *
 * A remount discards that element's internal state and any in-flight
 * animation, and drops focus if it held it. That is fine for stateless
 * chrome (buttons, menu rows, tiles) and wrong for text inputs or anything
 * mid-transition — use (1) there.
 *
 * @example
 * ```tsx
 * <Button
 *   key={restyleKey('options', menuOpen)}
 *   className={menuOpen ? 'bg-secondary-100 !shadow-none' : undefined}
 * />
 * ```
 *
 * @param id Stable identifier for the element (unique among its siblings).
 * @param states The values that drive the class change, in any order.
 */
export function restyleKey(
  id: string,
  ...states: (string | number | boolean | null | undefined)[]
): string {
  return states.length === 0 ? id : `${id}:${states.join('|')}`;
}
